import type { StripeSubscription } from "@core/types/auth"

/**
 * Plan d'abonnement d'une entreprise.
 */
export type SubscriptionPlan = "EM" | "EA" | "autre"

/**
 * Préfixes de NOM d'abonnement Stripe identifiant chaque famille.
 *
 * On identifie la famille par le nom et non par `metadata.tag` : les tags ne sont
 * pas fiables pour cet usage (des abonnements « Energy Analytics - … » portent en
 * base les tags `be`, `pv` ou `irve`), et la valeur du tag EA (`"energy-analytics"`)
 * ne correspond pas à la constante front historique (`"energyanalytics"`).
 */
export const PLAN_NAME_PREFIX = {
    EM: "Energy Management -",
    EA: "Energy Analytics -",
} as const

/** Statuts Stripe considérés comme donnant droit au plan. */
export const PLAN_ACTIVE_STATUSES = ["active", "past_due", "trialing"]

/** Abonnements écartés du calcul du plan (legacy, non utilisés). */
export const PLAN_UNWANTED_NAMES = [
    "Energy Management - Exploitation Chauffage",
]

/**
 * Détermine le plan d'une entreprise à partir de la LISTE BRUTE de ses abonnements
 * Stripe.
 *
 * Miroir exact de la SSOT backend
 * `api-metier/src/modules/vendors/stripe/stripe.helpers.js:getSubscriptionStatuAndPlan`.
 *
 * ⚠️ Ne jamais remplacer cet appel par une lecture du champ dénormalisé
 * `company.operation.stripe.plan` : ce champ n'a qu'un seul écrivain (un change
 * stream sans `.on('error')` ni `resumeAfter`, sans job de rattrapage), donc un
 * événement manqué le fige définitivement et silencieusement. C'est la cause de
 * DEV-1327 : Greenly, abonnement `Energy Analytics - 75 Sources` actif, servi avec
 * `plan: "autre"` — bouton de mise à jour et export absents de la page Données,
 * sans état désactivé ni message.
 *
 * @param subscriptions - Liste brute des abonnements Stripe de l'entreprise
 * @returns "EM" si un abonnement Energy Management donne droit, sinon "EA", sinon "autre"
 */
export function resolveSubscriptionPlan(
    subscriptions: StripeSubscription[] | null | undefined
): SubscriptionPlan {
    const eligible = (subscriptions ?? []).filter(
        (sub) =>
            PLAN_ACTIVE_STATUSES.includes(sub?.status) &&
            (sub?.name?.includes(PLAN_NAME_PREFIX.EM) ||
                sub?.name?.includes(PLAN_NAME_PREFIX.EA)) &&
            !PLAN_UNWANTED_NAMES.includes(sub?.name)
    )

    if (eligible.some((sub) => sub.name?.includes(PLAN_NAME_PREFIX.EM))) {
        return "EM"
    }
    if (eligible.some((sub) => sub.name?.includes(PLAN_NAME_PREFIX.EA))) {
        return "EA"
    }
    return "autre"
}

/**
 * Indique si une ligne d'abonnement donne un droit : statut `active`,
 * `past_due` ou `trialing` (DEV-2252, définition du client du 30/09/2026).
 * `canceled` et `incomplete_expired` (jamais payée) ne donnent rien.
 */
export function isLiveSubscription(
    subscription: Pick<StripeSubscription, "status"> | null | undefined
): boolean {
    return PLAN_ACTIVE_STATUSES.includes(subscription?.status ?? "")
}

/**
 * Définition du client (DEV-2252) : une entreprise est cliente si une de ses
 * lignes d'offre socle (« Energy Management - » ou « Energy Analytics - »,
 * hors « Energy Management - Exploitation Chauffage ») est vivante. Miroir de
 * `subscription_active` calculé par api-metier.
 */
export function hasLiveSocleSubscription(
    subscriptions: StripeSubscription[] | null | undefined
): boolean {
    return resolveSubscriptionPlan(subscriptions) !== "autre"
}

/**
 * Indique si une ligne VIVANTE porte le tag demandé (`metadata.tag`, liste
 * séparée par des virgules). Une ligne résiliée ou jamais payée n'ouvre aucun
 * module (DEV-2252).
 */
export function hasLiveSubscriptionWithTag(
    subscriptions: StripeSubscription[] | null | undefined,
    tag: string
): boolean {
    return (subscriptions ?? []).some(
        (subscription) =>
            isLiveSubscription(subscription) &&
            (
                subscription.metadata?.tag?.replace(/\s/g, "")?.split(",") ?? []
            ).includes(tag)
    )
}
